/**
 * personnelTransactionService — personel borç/alacak/maaş defteri için SQL-only adaptör.
 *
 * Eski RTDB `companies/{cid}/personnelTransactions/{personnelId}` karşılığı.
 * Backend: gts_accounting_api `PersonnelTransactionController` (atomik kasa/banka hareketi ile).
 *
 * KİMLİK: personnelId === SystemUsers Firebase uid (PersonnelFirebaseUid). SQL Id integer.
 * X-Company-Id `accountingApi` interceptor tarafından otomatik eklenir.
 */

import { accountingApi } from '../../config/apiClient';
import { ACCOUNTING_API } from '../../config/api';

export interface PersonnelTransactionDto {
  id: string;
  personnelId: string;
  personnelName: string;
  transactionType: 'DEBT' | 'RECEIVABLE' | 'PAYMENT' | 'COLLECTION';
  kind: string | null; // SALARY | BONUS | null
  paymentMethod: string; // accrual | cash | bank
  isAccrual: boolean;
  amount: number;
  currency: string;
  description: string;
  transactionDate: string;
  createdAt: string;
  performedBy: string;
  cashboxId: string | null;
  cashboxName: string;
  bankAccountId: string | null;
  bankSubAccountId: string | null;
  bankName: string;
  accrualId: string | null;
  isPaid: boolean;
  /** Hakediş tamamen ödendiğinde yazılır. */
  paidAt: string | null;
  /** Yalnız hakediş listesinde: bağlı ödemelerin toplamı ve kalan tutar. */
  paidAmount?: number;
  remainingAmount?: number;
  linkedCashboxTransactionId: number | null;
  linkedBankTransactionId: number | null;
}

export interface PersonnelBalance {
  currency: string;
  balance: number;
}

export interface CreatePersonnelTransactionPayload {
  personnelFirebaseUid: string;
  personnelName?: string;
  transactionType: 'DEBT' | 'RECEIVABLE' | 'PAYMENT' | 'COLLECTION';
  amount: number;
  currency: string;
  description?: string;
  transactionDate?: string; // ISO; yoksa backend bugünü kullanır
  paymentMethod: 'accrual' | 'cash' | 'bank';
  cashboxId?: number | null;
  bankAccountId?: number | null;
  bankSubAccountId?: number | null;
  accrualId?: number | null;
  performedBy?: string;
  /** ADVANCE = avans ödemesi işareti (maaş oluştururken avans uyarısı bu alana bakar). */
  kind?: 'ADVANCE';
  /** PYS: bağlanacak proje; backend türetilen kasa/banka hareketine de kopyalar. */
  projectId?: number | null;
}

export interface CreateAccrualPayload {
  personnelFirebaseUid: string;
  personnelName?: string;
  amount: number;
  currency: string;
  description?: string;
  kind: 'SALARY' | 'BONUS';
  transactionDate?: string;
  performedBy?: string;
  /** PYS: hakedişin bağlanacağı proje (maliyet olarak işler). */
  projectId?: number | null;
}

function num(v: any): number {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function mapTransaction(d: any): PersonnelTransactionDto {
  return {
    id: String(d?.id ?? ''),
    personnelId: d?.personnelFirebaseUid || '',
    personnelName: d?.personnelName || '',
    transactionType: d?.transactionType,
    kind: d?.kind ?? null,
    paymentMethod: d?.paymentMethod || 'accrual',
    isAccrual: !!d?.isAccrual,
    amount: num(d?.amount),
    currency: d?.currency || 'TRY',
    description: d?.description || '',
    transactionDate: d?.transactionDate || d?.createdAt || '',
    createdAt: d?.createdAt || '',
    performedBy: d?.performedBy || '',
    cashboxId: d?.cashboxId != null ? String(d.cashboxId) : null,
    cashboxName: d?.cashboxName || '',
    bankAccountId: d?.bankAccountId != null ? String(d.bankAccountId) : null,
    bankSubAccountId: d?.bankSubAccountId != null ? String(d.bankSubAccountId) : null,
    bankName: d?.bankAccountName || '',
    accrualId: d?.accrualId != null ? String(d.accrualId) : null,
    isPaid: !!d?.isPaid,
    paidAt: d?.paidAt || null,
    paidAmount: d?.paidAmount != null ? num(d.paidAmount) : undefined,
    remainingAmount: d?.remainingAmount != null ? num(d.remainingAmount) : undefined,
    linkedCashboxTransactionId: d?.linkedCashboxTransactionId ?? null,
    linkedBankTransactionId: d?.linkedBankTransactionId ?? null,
  };
}

/** Personel hareketleri (en yeni üstte). */
export async function getPersonnelTransactions(
  firebaseUid: string,
  page: number = 1,
  pageSize: number = 500
): Promise<{ data: PersonnelTransactionDto[]; totalCount: number }> {
  const res = await accountingApi.get(ACCOUNTING_API.PERSONNEL_TX_BY_PERSONNEL(firebaseUid, page, pageSize));
  const list = res?.data?.data ?? [];
  return {
    data: Array.isArray(list) ? list.map(mapTransaction) : [],
    totalCount: num(res?.data?.totalCount),
  };
}

/**
 * Personelin TÜM hareketleri (500'lük sayfalarla hepsi çekilir). Yürüyen bakiye ve avans
 * kontrolü tüm geçmişe ihtiyaç duyar; tek sayfa (500) sınırı eski hareketleri kesiyordu.
 */
export async function getAllPersonnelTransactions(firebaseUid: string): Promise<PersonnelTransactionDto[]> {
  const pageSize = 500;
  const all: PersonnelTransactionDto[] = [];
  for (let page = 1; page <= 200; page++) {
    const { data, totalCount } = await getPersonnelTransactions(firebaseUid, page, pageSize);
    all.push(...data);
    if (data.length < pageSize || all.length >= totalCount) break;
  }
  return all;
}

/** Para-birimi-bazlı bakiyeler (şirket perspektifi). */
export async function getPersonnelBalances(firebaseUid: string): Promise<PersonnelBalance[]> {
  const res = await accountingApi.get(ACCOUNTING_API.PERSONNEL_TX_BALANCE(firebaseUid));
  const list = res?.data?.data ?? [];
  return Array.isArray(list)
    ? list.map((b: any) => ({ currency: b?.currency || 'TRY', balance: num(b?.balance) }))
    : [];
}

/**
 * TÜM personelin para-birimi-bazlı bakiyeleri (şirket geneli, tek istek).
 * Personel listesi "Bakiye" sütunu için — uid -> PersonnelBalance[] eşlemesi döner.
 */
export async function getAllPersonnelBalances(): Promise<Record<string, PersonnelBalance[]>> {
  const res = await accountingApi.get(ACCOUNTING_API.PERSONNEL_TX_ALL_BALANCES);
  const list = res?.data?.data ?? [];
  const map: Record<string, PersonnelBalance[]> = {};
  if (Array.isArray(list)) {
    for (const b of list) {
      const uid = b?.personnelFirebaseUid || '';
      if (!uid) continue;
      (map[uid] ||= []).push({ currency: b?.currency || 'TRY', balance: num(b?.balance) });
    }
  }
  return map;
}

/** Ödenmemiş hakediş listesi (maaş/prim). */
export async function getAccruals(
  firebaseUid: string,
  kind?: 'SALARY' | 'BONUS',
  unpaidOnly: boolean = true
): Promise<PersonnelTransactionDto[]> {
  const res = await accountingApi.get(ACCOUNTING_API.PERSONNEL_TX_ACCRUALS(firebaseUid, kind, unpaidOnly));
  const list = res?.data?.data ?? [];
  return Array.isArray(list) ? list.map(mapTransaction) : [];
}

/** Borç/Alacak/Ödeme/Tahsilat oluştur (cash/bank ise atomik kasa-banka hareketiyle). */
export async function createPersonnelTransaction(
  payload: CreatePersonnelTransactionPayload
): Promise<PersonnelTransactionDto> {
  const res = await accountingApi.post(ACCOUNTING_API.PERSONNEL_TX_CREATE, payload);
  return mapTransaction(res?.data?.data);
}

/** Maaş/prim hakedişi oluştur (para hareketi yok). */
export async function createAccrual(payload: CreateAccrualPayload): Promise<PersonnelTransactionDto> {
  const res = await accountingApi.post(ACCOUNTING_API.PERSONNEL_TX_CREATE_ACCRUAL, payload);
  return mapTransaction(res?.data?.data);
}

/** Hakedişi ödendi işaretle. */
export async function markAccrualPaid(id: number, paidBy?: string): Promise<void> {
  await accountingApi.post(ACCOUNTING_API.PERSONNEL_TX_MARK_PAID, { id, paidBy });
}

/**
 * Personel hareketini siler. Bağlı kasa/banka hareketi varsa backend onları da
 * bulup geri alır (bakiye ters işlenir, hareket silinir, log yazılır) ve hakediş
 * ödemesiyse ilgili hakediş tekrar "ödenmedi" durumuna çekilir.
 */
export async function deletePersonnelTransaction(id: number, deletedBy?: string): Promise<void> {
  await accountingApi.post(ACCOUNTING_API.PERSONNEL_TX_DELETE, { id, deletedBy });
}
